import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Container, Typography, CircularProgress, Button } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

const PublicRecipe = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const [recipe, setRecipe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        fetch(`${process.env.REACT_APP_BACKEND_ADDRESS}/public/recipe/${token}`)
            .then(res => {
                if (!res.ok) { setNotFound(true); return null; }
                return res.json();
            })
            .then(data => { if (data) setRecipe(data.recipe); })
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
            <CircularProgress />
        </Box>
    );

    if (notFound) return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
            <Typography variant="h5" fontWeight={700}>Recipe not found</Typography>
            <Typography color="text.secondary">This link may have been revoked or doesn't exist.</Typography>
            <Button variant="contained" onClick={() => navigate('/login')}>Sign in to Cookbook</Button>
        </Box>
    );

    const groupByCategory = (items, key = 'category_name') =>
        (items || []).reduce((acc, item) => {
            const cat = item[key] || '';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(item);
            return acc;
        }, {});

    const groupedIngredients = groupByCategory(recipe.ingredients);
    const groupedDirections = groupByCategory(recipe.directions);
    const directionEntries = Object.entries(groupedDirections).sort(([a], [b]) => {
        if (!a) return 1; if (!b) return -1; return a.localeCompare(b);
    });

    const authorLine = recipe.authors?.length > 0
        ? 'Recipe Courtesy of ' + recipe.authors.map(a => a.display_name).join(', ')
        : recipe.author ? `Recipe Courtesy of ${recipe.author}` : null;

    const meta = [
        recipe.prep_time && `Prep Time: ${recipe.prep_time}`,
        recipe.recipe_yield && `Yield: ${recipe.recipe_yield}`,
    ].filter(Boolean).join('  ·  ');

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
            {/* Banner */}
            <Box sx={{ bgcolor: 'primary.main', color: '#fff', py: 1, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="img" src="/mangiamangia-icon.jpg" sx={{ width: 28, height: 28, borderRadius: 0.5, objectFit: 'cover' }} />
                    <Typography variant="body2" fontWeight={700}>Sposito Family Cookbook</Typography>
                </Box>
                <Button size="small" variant="outlined" onClick={() => navigate('/login')}
                    sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', fontSize: 12 }}>
                    <LockIcon sx={{ fontSize: 14, mr: 0.5 }} />Sign in
                </Button>
            </Box>

            <Container maxWidth="md" sx={{ py: 4 }}>
                {/* Header */}
                <Box sx={{ display: 'flex', gap: 3, mb: 3, alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight={700} gutterBottom>{recipe.title}</Typography>
                        {authorLine && <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 0.5 }}>{authorLine}</Typography>}
                        {meta && <Typography variant="body2" color="text.secondary">{meta}</Typography>}
                    </Box>
                    {recipe.download_image_url && (
                        <Box component="img" src={recipe.download_image_url} alt={recipe.title}
                            sx={{ width: { xs: 100, sm: 160 }, height: { xs: 100, sm: 160 }, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
                    )}
                </Box>

                <Box sx={{ borderBottom: '2px solid', borderColor: 'divider', mb: 3 }} />

                {recipe.recipe_description && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="overline" fontWeight={700} display="block" sx={{ mb: 1 }}>Description</Typography>
                        <Typography variant="body1">{recipe.recipe_description}</Typography>
                    </Box>
                )}

                <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', sm: 'row' } }}>
                    {/* Ingredients */}
                    {recipe.ingredients?.length > 0 && (
                        <Box sx={{ flex: '0 0 auto', width: { xs: '100%', sm: '36%' } }}>
                            <Typography variant="overline" fontWeight={700} display="block" sx={{ mb: 1 }}>Ingredients</Typography>
                            {Object.entries(groupedIngredients).map(([cat, ings]) => (
                                <Box key={cat} sx={{ mb: 1.5 }}>
                                    {cat && cat !== 'Uncategorized' && <Typography variant="body2" fontWeight={700} sx={{ fontStyle: 'italic', mb: 0.5 }}>{cat}</Typography>}
                                    <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                        {ings.map((ing, i) => (
                                            <Typography component="li" variant="body2" key={i} sx={{ mb: 0.5 }}>
                                                {[ing.quantity, ing.unit, ing.ingredient].filter(Boolean).join(' ')}
                                            </Typography>
                                        ))}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Directions */}
                    {recipe.directions?.length > 0 && (
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="overline" fontWeight={700} display="block" sx={{ mb: 1 }}>Directions</Typography>
                            {directionEntries.map(([cat, dirs]) => {
                                const sorted = [...dirs].sort((a, b) => (a.direction_order ?? 0) - (b.direction_order ?? 0));
                                return (
                                    <Box key={cat} sx={{ mb: 2 }}>
                                        {cat && cat !== 'Uncategorized' && <Typography variant="body2" fontWeight={700} sx={{ fontStyle: 'italic', mb: 0.5 }}>{cat}</Typography>}
                                        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                                            {sorted.map((dir, i) => (
                                                <Typography component="li" variant="body2" key={i} sx={{ mb: 1, lineHeight: 1.6 }}>
                                                    {dir.direction}
                                                </Typography>
                                            ))}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

export default PublicRecipe;
